import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merges Tailwind CSS class names, resolving conflicts intelligently.
 *
 * Args:
 *   inputs: Class values to merge (strings, conditionals, arrays).
 *
 * Returns:
 *   A single merged class string with Tailwind conflicts resolved.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Truncates a string to the specified length, appending an ellipsis.
 *
 * Args:
 *   str: The string to truncate.
 *   maxLength: Maximum character length before truncation.
 *
 * Returns:
 *   The truncated string with "..." suffix, or the original if within limit.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength)}...`;
}

/**
 * Validates that a string is a valid SHA-256 hex hash (exactly 64 lowercase hex chars).
 *
 * Args:
 *   value: The string to validate.
 *
 * Returns:
 *   True if the string is a valid SHA-256 hex hash.
 */
export function isValidSha256(value: string): boolean {
  return /^[0-9a-f]{64}$/.test(value);
}

/**
 * Validates that a string is a valid UUID v4.
 *
 * Args:
 *   value: The string to validate.
 *
 * Returns:
 *   True if the string is a valid UUID.
 */
export function isValidUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}
